import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../../context/UserContext';
import PageTabs from '../shared/PageTabs';
import { buildCourseTabs } from '../shared/tabConfigs';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  LinearProgress,
  Tooltip,
  InputAdornment,
  Checkbox,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Add as AddIcon, 
  Refresh as RefreshIcon, 
  Check as CheckIcon, 
  Close as CloseIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { courseService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import EnrollLearnersDialog from './EnrollLearner';
import ConfirmDialog from '../common/ConfirmDialog';
import { formatDate } from '../../utils/dateFormatter';
import { tokens } from '../../theme';
import EmptyState from '../common/EmptyState';
const CourseLearners = () => {
  const { id: courseId } = useParams();
  const { showToast } = useToast();
  const { user } = useUser();
  const courseTabs = buildCourseTabs(courseId, { permissions: user?.permissions || [], roleId: user?.role_id });
  const [courseInfo, setCourseInfo] = useState(null);
  const [instances, setInstances] = useState([]);
  const [learners, setLearners] = useState([]);
  const [totalLearners, setTotalLearners] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstance, setSelectedInstance] = useState('');
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState('');
  const [selectedLearnerId, setSelectedLearnerId] = useState(null);
  const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
  const [confirmDialogContent, setConfirmDialogContent] = useState('');

  const [selectedLearners, setSelectedLearners] = useState([]);
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
  const isActionsMenuOpen = Boolean(actionsMenuAnchorEl);
  // Fetch course instances
  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const courseResponse = await courseService.getCourseDetails(courseId);
        setCourseInfo(courseResponse.data.data);
        if(courseResponse.data.data.instances){
            setInstances(courseResponse.data.data.instances);
        }
        else {
            console.error('Unexpected instances response format:');
            setInstances([]);
        }
        setDataInitialized(true);
      } catch (error) {
        console.error('Error fetching course data:', error);
        showToast('Failed to load course data', 'error');
        setInstances([]);
        setDataInitialized(true);
      }
    };
    
    fetchCourseData();
  }, [courseId, showToast]);
  
  // Fetch learners with pagination and filters
  const fetchLearners = useCallback(async () => {
    if (!dataInitialized) return;
    try {
      const response = await courseService.getCourseLearners(courseId, {
        draw: 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        instance_id: selectedInstance,
        search_term: searchTerm
      });
      
      setLearners(response.data.data);
      setTotalLearners(response.data.recordsTotal);
    } catch (error) {
      console.error('Error fetching learners:', error);
      showToast('Failed to load learners', 'error');
    }
  }, [courseId, page, rowsPerPage, selectedInstance, searchTerm, showToast, dataInitialized]);
 
  // Fetch learners when filters change
  useEffect(() => {
    if (dataInitialized) {
      fetchLearners();
    }
  }, [fetchLearners, dataInitialized]);
  
  useEffect(() => {
    setSelectedLearners([]);
  }, [page, rowsPerPage, searchTerm, selectedInstance]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  
  // Handle instance filter change
  const handleInstanceChange = (event) => {
    setSelectedInstance(event.target.value);
    setPage(0);
  };
  
  const handleSelectLearner = (learnerId) => {
    setSelectedLearners(prev => {
      if (prev.includes(learnerId)) {
        return prev.filter(id => id !== learnerId);
      } else {
        return [...prev, learnerId];
      }
    });
  };
  const handleSelectAllLearners = (event) => {
    if (event.target.checked && learners) {
      const currentPageLearnerIds = learners.map(learner => learner.id);
      setSelectedLearners(currentPageLearnerIds);
    } else {
      setSelectedLearners([]);
    }
  };

  const isAllSelected = learners && learners.length > 0 && selectedLearners.length === learners.length;
  const handleActionsMenuClick = (event) => {
    setActionsMenuAnchorEl(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchorEl(null);
  };

  const openConfirmDialog = (action, learnerId) => {
    setSelectedLearnerId(learnerId);
    setConfirmAction(action);
    let title = '';
    let content = '';
    switch(action) {
      case 'unenroll':
        title = 'Unenroll Learner';
        content = learnerId 
          ? 'Are you sure you want to unenroll this learner? This action cannot be undone.'
          : `Are you sure you want to unenroll ${selectedLearners.length} learner(s)? This action cannot be undone.`;
        break;
      case 'markComplete':
        title = 'Mark All Modules Complete';
        content = learnerId
          ? 'Are you sure you want to mark all modules as complete for this learner?'
          : `Are you sure you want to mark all modules as complete for ${selectedLearners.length} learner(s)?`;
        break;
      case 'resetModules':
        title = 'Reset All Modules';
        content = learnerId
          ? 'Are you sure you want to reset all modules for this learner? This will remove all progress data.'
          : `Are you sure you want to reset all modules for ${selectedLearners.length} learner(s)? This will remove all progress data.`;
        break;
      default:
        break;
    }
    
    setConfirmDialogTitle(title);
    setConfirmDialogContent(content);
    setConfirmDialogOpen(true);
    handleActionsMenuClose();
  };

  const handleConfirmAction = async () => {
    setConfirmDialogOpen(false);
    
    try {
      let response;
      const learnerIds = selectedLearnerId ? [selectedLearnerId] : selectedLearners;
      switch(confirmAction) {
        case 'unenroll':
          if (selectedLearnerId) {
            response = await courseService.unenrollLearner(selectedLearnerId);
          } else {
            response = await courseService.bulkUnenrollLearners(learnerIds);
          }
          if (response.data.status === 'success') {
            showToast(response.data.message || 'Learner unenrolled successfully', 'success');
          }
          break;
        case 'markComplete':
          if (selectedLearnerId) {
            response = await courseService.markLearnerCompletion(selectedLearnerId);
          } else {
            response = await courseService.bulkMarkLearnerCompletion(learnerIds);
          }
          if (response.data.status === 'success') {
            showToast(response.data.message || 'All modules marked as complete', 'success');
          }
          break;
        case 'resetModules':
          if (selectedLearnerId) {
            response = await courseService.resetLearnerModules(selectedLearnerId);
          } else {
            response = await courseService.bulkResetLearnerModules(learnerIds);
          }
          if (response.data.status === 'success') {
            showToast(response.data.message || 'All modules reset successfully', 'success');
          }
          break;
        default:
          return;
      }
      
      fetchLearners();
      setSelectedLearners([]);
    } catch (error) {
      console.error(`Error performing ${confirmAction} action:`, error);
      showToast(`Failed to ${confirmAction.replace(/([A-Z])/g, ' $1').toLowerCase()} learner(s)`, 'error');
    }
  };
  
  // Handle download Excel
  const handleDownloadExcel = async () => {
    try {
      const response = await courseService.exportLearnersData(
        courseId, 
        selectedInstance, 
        searchTerm
      );
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'course_learners.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading Excel:', error);
      showToast('Failed to download learners data', 'error');
    }
  };
  
  // Handle enroll dialog close
  const handleEnrollDialogClose = (success) => {
    setEnrollDialogOpen(false);
    if (success) {
      fetchLearners();
    }
  };
  
  return (
    <Box sx={{ p: 2 }}>
        <PageTabs tabs={courseTabs} />
        <Typography variant="h4" gutterBottom className='page-title' sx={{ marginBottom: '2em'}} >Enrolled Learners</Typography>        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              variant="outlined"
              size="small"
              value={searchTerm}
              // className="custom-search-field"
              className="pink-filter"
              sx={{height: '35px'}}
              onChange={handleSearchChange}
              placeholder="Name, Email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
           />
           {/* <FormControl variant="outlined" size="small" sx={{ width: 200 }}>
            <Select
              labelId="instance-filter-label"
              value={selectedInstance}
              onChange={handleInstanceChange}
              className="pink-filter"
              label="Instance"
              displayEmpty
            >
              <MenuItem value="">All Instances</MenuItem>
              {instances.map((instance) => (
                <MenuItem key={instance.id} value={instance.id}>
                  {instance.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
        </Box>
        <Box>
          {selectedLearners.length > 0 && (
            <Button 
              variant="outlined" 
              onClick={handleActionsMenuClick}
              aria-controls={isActionsMenuOpen ? 'actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isActionsMenuOpen ? 'true' : undefined}
              sx={{ mr: 1 }}
            >
              Actions
            </Button>
          )}
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />} 
            onClick={handleDownloadExcel}
            sx={{ mr: 1 }}
          >
            Export Learners
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={() => setEnrollDialogOpen(true)}
          >
            Enroll Learners
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6">
            Total Learners: {totalLearners}
          </Typography>
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: '40px', pl: 0.5 }}>
                  <Checkbox
                    indeterminate={selectedLearners.length > 0 && !isAllSelected}
                    checked={isAllSelected}
                    onChange={handleSelectAllLearners}
                    className='table-checkboxes'
                    size='small'
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                {/* <TableCell>Instance</TableCell> */}
                <TableCell>Progress</TableCell>
                <TableCell>Enrollment Type</TableCell>
                <TableCell>Enrolled Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                { learners === null ? (
                  null // Don't show anything while loading
                ) : learners.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={7} align="center">
                          {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14): a plain line. */}
                          <EmptyState framed={false} title="No learners found" message={null} />
                        </TableCell>
                    </TableRow>
                ) : (
                    learners.map((learner) => (
                        <TableRow key={learner.id}>
                          <TableCell padding="checkbox" sx={{ width: '40px', pl: 0.5 }}>
                            <Checkbox
                              checked={selectedLearners.includes(learner.id)}
                              onChange={() => handleSelectLearner(learner.id)}
                              className='table-checkboxes'
                              size='small'
                            />
                          </TableCell>
                          <TableCell>{learner.name}</TableCell>
                          <TableCell>{learner.email}</TableCell>
                          {/* <TableCell>{learner.instance}</TableCell> */}
                          <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                    <LinearProgress 
                                    variant="determinate" 
                                    value={learner.progress} 
                                    sx={{ 
                                      height: 8, 
                                      borderRadius: 3,
                                      backgroundColor: tokens.color.bgTertiary,
                                      '& .MuiLinearProgress-bar': {
                                        backgroundColor: learner.progress >= 100 ? tokens.color.positive : tokens.color.brand,                                        
                                      },
                                    }}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                    <Typography variant="body2" color="text.secondary">
                                    {`${learner.progress}%`}
                                    </Typography>
                                </Box>
                              </Box>
                          </TableCell>
                          <TableCell>{learner.enrollment_type}</TableCell>
                          <TableCell>{formatDate(learner.date_enrolled)}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Tooltip title="Mark Complete">
                                <IconButton 
                                    size="small" 
                                    onClick={() => openConfirmDialog('markComplete', learner.id)}
                                >
                                    <CheckIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reset Modules">
                                <IconButton 
                                    size="small" 
                                    onClick={() => openConfirmDialog('resetModules',learner.id)}
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Unenroll">
                                <IconButton 
                                    size="small" 
                                    onClick={() => openConfirmDialog('unenroll',learner.id)}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                    ))
                )}
            </TableBody>
          </Table>
                        
          <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalLearners}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>


        <Menu
          id="actions-menu"
          anchorEl={actionsMenuAnchorEl}
          open={isActionsMenuOpen}
          onClose={handleActionsMenuClose}
          MenuListProps={{
            'aria-labelledby': 'actions-button',
          }}
        >
          <MenuItem onClick={() => openConfirmDialog('unenroll')}>
            <ListItemIcon>
              <CloseIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Unenroll </ListItemText>
          </MenuItem>
          <MenuItem onClick={() => openConfirmDialog('markComplete')}>
            <ListItemIcon>
              <CheckIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mark All Modules Complete</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => openConfirmDialog('resetModules')}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Reset All Modules</ListItemText>
          </MenuItem>          
        </Menu>

        {/* Enroll Learners Dialog */}
        <EnrollLearnersDialog
        open={enrollDialogOpen}
        onClose={handleEnrollDialogClose}
        courseId={courseId}
        instances={instances}
        fetchLearners={fetchLearners}
        />
        {/* Confirmation Dialog */}
        <ConfirmDialog
          open={confirmDialogOpen}
          title={confirmDialogTitle}
          content={confirmDialogContent}
           confirmLabel={
            confirmAction === 'unenroll' ? 'Unenroll' :
            confirmAction === 'markComplete' ? 'Mark complete' :
            confirmAction === 'resetModules' ? 'Reset modules' :
            'Confirm'
          }
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmDialogOpen(false)}
        />
    </Box>
    );
};

export default CourseLearners;
                